import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { TwoFactorAuthEvents } from '@pages/TwoFactorAuth/useTwoFactorAuth'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { PageSection } from '@ui/components/PageSection'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { getVerifiedPhoneNumbers } from '@model/AccountDetailedDto'
import { NameDto } from '@model/NameDto'
import {
  Change2FAProvidersData,
  GoogleAuthenticator2FAProviderDataDto,
  TwoFAProviderEnum,
  TwoFAProviderItem,
  VerifyFirstTimeUseData,
} from '@model/TwoFactorAuthentication'

import { useLogout } from '@hooks/useLogout'
import { ScrollToIds } from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { SupportModal } from '@shared/modals/Support.modal'

import { handleBaseTwoFactorErrors } from '../handleBaseTwoFactorErrors'
import { ChangePasswordFormValues } from './ChangePassword/ChangePasswordForm'
import { ConfirmTurnOffTwoFactorAuthenticationModal } from './modals/ConfirmTurnOffTwoFactorAuthentication.modal'
import { GoogleAuthenticatorModal } from './modals/GoogleAuthenticator.modal'
import { RecoveryCodesModal } from './modals/RecoveryCodes.modal'
import { TwoFactorAuthenticationSelectPhoneModal } from './modals/TwoFactorAuthenticationSelectPhone.modal'

export enum AuthActionIds {
  Login = 1,
  Withdraw = 2,
  PasswordChange = 3,
}

export type SecuritySettingsOutletCtx = {
  activeProviders: TwoFAProviderItem[]
  twoFAProvidersTypes: NameDto[]
  twoFAProvidersActionTypes: NameDto[]
  onProviderToggle: (provider: NameDto, turnOn: boolean) => void
  onAuthActionsProviderToggle: (authAction: NameDto, turnOn: boolean) => void
  onPhoneNumberChange: (phone: string, provider: NameDto) => void
  onGenerateRecoveryCodes: () => void
  onSubmitChangePassword: (values: ChangePasswordFormValues) => Promise<void>
  isTwoFAProvidersLoading: boolean
  account: any
}

export const SecuritySettingsPage: React.FC = () => {
  useScrollToTop()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { logout } = useLogout()
  const { account } = useAccountReadContext()
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const isMobile = useWindowResize()

  const location = useLocation()

  const [activeProviders, setActiveProviders] = useState<TwoFAProviderItem[]>([])
  const [selectedProvider, setSelectedProvider] = useState<NameDto>()
  const [selectedAuthAction, setSelectedAuthAction] = useState<NameDto>()
  const [hasShownInitialRecoveryCodes, setHasShownInitialRecoveryCodes] = useState(false)

  const [selectedPhone, setSelectedPhone] = useState<string>()
  const [googleAuthenticatorError, setGoogleAuthenticatorError] = useState(false)
  const [isGoogleAuthenticatorDataLoading, setIsGoogleAuthenticatorDataLoading] =
    useState<boolean>(false)

  const googleAuthModal = useModal(GoogleAuthenticatorModal)
  const confirmTurnOffModal = useModal(ConfirmTurnOffTwoFactorAuthenticationModal)
  const [canCloseRecoveryCodesModal, setCanCloseRecoveryCodesModal] = useState(false)

  const recoveryCodesModal = useModal(RecoveryCodesModal, {
    onClose: () => setCanCloseRecoveryCodesModal(true),
  })

  const [isTwoFactorFunctionalityEnabled, setIsTwoFactorFunctionalityEnabled] = useState(true)
  const verifiedPhones = getVerifiedPhoneNumbers(account)
  const email = useMemo(() => account?.emails.find((x) => x.isPrimary)?.address, [account])
  const [previousActive2FAProviders, setPreviousActive2FAProviders] =
    useState<TwoFAProviderItem[]>()

  const passwordChangedModal = useModal(SupportModal, {
    title: t('Profile.Password Changed'),
    description: () => (
      <span
        dangerouslySetInnerHTML={{
          __html: t('Profile.Change success'),
        }}
      />
    ),
  })
  const twoFASelectPhoneModal = useModal(TwoFactorAuthenticationSelectPhoneModal, {
    selectedPhone,
    phoneNumbers: verifiedPhones,
    onConfirm: (phone) => {
      setSelectedPhone(phone)
      handle2FAProviderSelect(
        true,
        twoFAProvidersTypes.find((provider) => provider.id === TwoFAProviderEnum.SMS),
        phone
      )
    },
  })

  useEffect(() => {
    const state = location.state || {}
    const {
      isPasswordTabSelected,
      is2FATabSelected,
      isTooManyCodeInsertAttempts,
      isTooManyResendAttempts,
      newCodeCantBeSent,
      cooldownInMinutes,
      cooldownInSeconds,
    } = state

    if (isPasswordTabSelected) {
      navigate('password-change', { replace: true })
    } else if (is2FATabSelected) {
      navigate('2fa', { replace: true })
    } else if (isTooManyCodeInsertAttempts && cooldownInMinutes) {
      addSnackbar.error({
        message: t(
          'TwoFactorAuth.2FA max insert code attempts reached. Logging in to your account is suspended for the remaining duration of X minutes',
          { minutes: cooldownInMinutes }
        ),
      })
    } else if (isTooManyResendAttempts && cooldownInMinutes) {
      addSnackbar.error({
        message: t(
          "TwoFactorAuth.You've reached the maximum amount of verification attempts. Try again in X minutes",
          { minutes: cooldownInMinutes }
        ),
      })
    } else if (newCodeCantBeSent && cooldownInSeconds) {
      addSnackbar.error({
        message: t(
          'TwoFactorAuth.2FA requesting a new one time code cannot executed for the next {{ seconds }} seconds',
          { minutes: cooldownInSeconds }
        ),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const { data: twoFAProvidersTypes = [], isLoading: isTwoFAProvidersTypesLoading } = useQuery({
    queryKey: queryKeys.profile.twoFAProviderTypes(),
    queryFn: async () => {
      const response = await apiClient.get2FAProviderTypes()
      if (!response?.length) {
        navigate('password-change', { replace: true })
      }
      return response
    },
  })

  const fetch2FAProviders = useCallback(async () => {
    try {
      const twoFAProviders = await apiClient.get2FAProviders()
      setActiveProviders(twoFAProviders)
      setPreviousActive2FAProviders(twoFAProviders)

      return twoFAProviders
    } catch (error: unknown) {
      if (instanceOfAxiosError((error as ResponseError)?.response)) {
        const errorResponse = (error as ResponseError).response.response?.data

        if (
          'two_factor_authentication_disabled_for_county_of_residence_of_user' ===
          errorResponse?.code
        ) {
          setIsTwoFactorFunctionalityEnabled(false)
          navigate('password-change', { replace: true })
        }
        throw error
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isLoading: isTwoFAProvidersLoading } = useQuery({
    queryKey: queryKeys.profile.twoFAProviders(),
    queryFn: fetch2FAProviders,
    refetchOnMount: 'always',
  })

  const { data: twoFAProvidersActionTypes = [], isLoading: isTwoFAProvidersActionTypesLoading } =
    useQuery({
      queryKey: queryKeys.profile.twoFAProviderActionTypes(),
      queryFn: () => apiClient.get2FAProviderActionTypes(),
    })

  useEffect(() => {
    if (
      location.state?.isProviderEnabled &&
      location.state?.previousActive2FAProviders &&
      !hasShownInitialRecoveryCodes
    ) {
      const hasNoPreviousActiveProviders = location.state.previousActive2FAProviders.length === 0

      if (hasNoPreviousActiveProviders && activeProviders.length > 0) {
        setPreviousActive2FAProviders(activeProviders)
        setHasShownInitialRecoveryCodes(true)
        recoveryCodesModal.open({ initialCanClose: canCloseRecoveryCodesModal })

        navigate('.', {
          replace: true,
          state: {},
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.state?.isProviderEnabled,
    location.state?.previousActive2FAProviders,
    activeProviders,
    hasShownInitialRecoveryCodes,
  ])

  const handleCloseGoogleAuthenticatorModal = () => {
    setGoogleAuthenticatorError(false)
    setSelectedProvider(undefined)
  }

  const getGoogleAuthenticatorData =
    async (): Promise<GoogleAuthenticator2FAProviderDataDto | null> => {
      try {
        const payload = { typeId: TwoFAProviderEnum.GoogleAuthenticator }
        setIsGoogleAuthenticatorDataLoading(true)

        return await apiClient.enableGoogleAuthenticator2FAProviders(payload)
      } catch (err: ResponseError | any) {
        console.error(err)
        return null
      } finally {
        setIsGoogleAuthenticatorDataLoading(false)
      }
    }

  const clearSessionAndLogout = async () => {
    logout()
    navigate('/logout')
  }

  const onClosePasswordModal = async () => {
    await clearSessionAndLogout()
  }

  const onProviderToggle = async (provider: NameDto, turnOn: boolean) => {
    setSelectedProvider(provider)
    if (!turnOn) {
      confirmTurnOffModal.open({
        onConfirm: () => {
          handle2FAProviderSelect(false, provider)
        },
      })
    } else {
      switch (provider.id) {
        case TwoFAProviderEnum.GoogleAuthenticator: {
          const data = await getGoogleAuthenticatorData()
          if (data) {
            googleAuthModal.open({
              data,
              isLoading: isGoogleAuthenticatorDataLoading,
              isError: googleAuthenticatorError,
              onSubmit: handleGoogleAuthenticatorSubmit,
              onClose: handleCloseGoogleAuthenticatorModal,
            })
          }
          break
        }
        case TwoFAProviderEnum.Email:
          handle2FAProviderSelect(true, provider)
          break
        case TwoFAProviderEnum.SMS: {
          const first = verifiedPhones?.[0]
          const initial = first ? `${first.countryCode}${first.number}` : ''
          twoFASelectPhoneModal.open({ selectedPhone: initial })
          break
        }
        default:
          break
      }
    }
  }

  const onAuthActionsProviderToggle = async (turnOn: boolean, authAction?: NameDto) => {
    const selectedAuthActionType = selectedAuthAction ?? authAction
    const isGoogleAuthProviderActive = activeProviders.find(
      (activeProvider) => activeProvider.type.id === TwoFAProviderEnum.GoogleAuthenticator
    )

    try {
      if (selectedAuthActionType) {
        const payload = { actionTypeId: selectedAuthActionType.id }
        const response = turnOn
          ? await apiClient.enable2FAProvidersActionType(payload)
          : await apiClient.disable2FAProvidersActionType(payload)
        await queryClient.invalidateQueries({ queryKey: ['profile'] })

        if (response && response.twoFactor) {
          const hasTwoFactor = response.twoFactor.length > 0

          if (hasTwoFactor) {
            const authMediumFallback = isGoogleAuthProviderActive
              ? t('Profile.Google Authenticator app')
              : ''

            navigate('/2fa/confirm', {
              state: {
                twoFactorAuthData: {
                  twoFactorAuthEvent: turnOn
                    ? TwoFactorAuthEvents.EnableAuthAction
                    : TwoFactorAuthEvents.DisableAuthAction,
                  twoFactor: isGoogleAuthProviderActive
                    ? [
                        {
                          ...response.twoFactor[0],
                          authMedium: authMediumFallback,
                        },
                      ]
                    : response.twoFactor,
                  actionTypeId: selectedAuthActionType.id,
                },
              },
            })
          }
        } else {
          fetch2FAProviders()
        }
      }
    } catch (error: unknown) {
      const isErrorCaught = handleBaseTwoFactorErrors(error, navigate)
      if (!isErrorCaught) {
        throw error
      }
    } finally {
      setSelectedAuthAction(undefined)
    }
  }

  const get2FAProviderAuthMediumFallback = (id: number) => {
    const isSMS2FAProvider = id === TwoFAProviderEnum.SMS && selectedPhone
    const isGoogleAuthProvider = id === TwoFAProviderEnum.GoogleAuthenticator

    if (isSMS2FAProvider) {
      return selectedPhone
    } else if (isGoogleAuthProvider) {
      return t('Profile.Google Authenticator app')
    } else {
      return email
    }
  }

  const handleSubmitChangePassword = async (values: ChangePasswordFormValues) => {
    try {
      const response = await apiClient.updatePassword(values)
      const hasTwoFactor = (response?.twoFactor?.length ?? 0) > 0

      if (hasTwoFactor) {
        const googleAuthProvider = response.twoFactor?.find(
          (twoFactor) => twoFactor?.provider?.id === TwoFAProviderEnum.GoogleAuthenticator
        )

        const authMedium = googleAuthProvider
          ? get2FAProviderAuthMediumFallback(googleAuthProvider.provider.id)
          : response.twoFactor?.[0]?.authMedium

        navigate('/2fa/confirm', {
          state: {
            twoFactorAuthData: {
              twoFactorAuthEvent: TwoFactorAuthEvents.ChangePassword,
              twoFactor: googleAuthProvider
                ? [{ ...response.twoFactor?.[0], authMedium }]
                : response.twoFactor,

              email: response.email,
              password: values.currentPassword,
              newPassword: values.newPassword,
            },
          },
        })
      } else {
        addSnackbar.success({ message: t('Password has been changed') })
        passwordChangedModal.open({ onClose: onClosePasswordModal })
      }
    } catch (error: unknown) {
      const isErrorCaught = handleBaseTwoFactorErrors(error, navigate)
      if (!isErrorCaught) {
        throw error
      }
    }
  }

  const handleProviderToggle = async (
    data: Change2FAProvidersData,
    isEnabled: boolean,
    phoneOverride?: string
  ) => {
    const isSMS = data.type.id === TwoFAProviderEnum.SMS
    const authMedium = isSMS ? phoneOverride ?? selectedPhone : undefined

    try {
      const response = isEnabled
        ? await apiClient.enable2FAProviders({
            typeId: data.type.id,
            ...(isSMS && authMedium ? { authMedium } : {}),
          })
        : await apiClient.disable2FAProviders({
            typeId: data.type.id,
            ...(isSMS && authMedium ? { authMedium } : {}),
          })
      await queryClient.invalidateQueries({ queryKey: ['profile'] })

      if (isEnabled && 'data' in response) {
        const twoFactorAuthData = {
          twoFactorAuthEvent: TwoFactorAuthEvents.EnableProvider,
          twoFactor: [response?.data],
          previousActive2FAProviders: previousActive2FAProviders,
        }

        navigate('/2fa/confirm', {
          state: {
            twoFactorAuthData,
          },
        })
      } else if ('twoFactor' in response) {
        const targetTwoFactorProvider = response?.twoFactor?.find(
          (twoFactor) => twoFactor.provider.id === data.type.id
        )
        const remainingActiveTwoFactorProviders = response?.twoFactor?.filter(
          (twoFactor) => twoFactor.provider.id !== data.type.id
        )

        // Move target (selected) provider to the 1st index to make sure correct ID is passed to the request
        // in case if there are multiple providers active (Email + SMS)
        const rearrangedTwoFactors = targetTwoFactorProvider
          ? [targetTwoFactorProvider, ...remainingActiveTwoFactorProviders]
          : response.twoFactor

        const isGoogleAuthProviderActive = activeProviders.find(
          (activeProvider) => activeProvider.type.id === TwoFAProviderEnum.GoogleAuthenticator
        )

        const twoFactorAuthData = {
          twoFactorAuthEvent: TwoFactorAuthEvents.DisableProvider,
          twoFactor: isGoogleAuthProviderActive
            ? [{ ...rearrangedTwoFactors[0], authMedium }]
            : rearrangedTwoFactors,
        }

        navigate('/2fa/confirm', {
          state: {
            twoFactorAuthData,
          },
        })
      }
    } catch (error: unknown) {
      const isErrorCaught = handleBaseTwoFactorErrors(error, navigate)
      if (!isErrorCaught) {
        throw error
      }
    } finally {
      setSelectedProvider(undefined)
    }
  }

  const handleGoogleAuthenticatorSubmit = async (code: string) => {
    if (!code) return

    const payload: VerifyFirstTimeUseData = {
      typeId: TwoFAProviderEnum.GoogleAuthenticator,
      code,
    }

    try {
      setIsGoogleAuthenticatorDataLoading(true)

      await apiClient.verify2FACodesFirstTimeUse(payload)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await fetch2FAProviders()

      googleAuthModal.close()

      navigate('.', {
        state: {
          isProviderEnabled: true,
          previousActive2FAProviders,
        },
      })
    } catch (err: ResponseError | any) {
      console.error(err)
      setGoogleAuthenticatorError(true)
    } finally {
      setIsGoogleAuthenticatorDataLoading(false)
    }
  }

  const handle2FAProviderSelect = async (
    isOn: boolean,
    provider?: NameDto,
    phoneFromModal?: string
  ) => {
    const selectedType = selectedProvider ?? provider
    if (!selectedType) return

    const payload = { type: selectedType }
    await handleProviderToggle(payload, isOn, phoneFromModal)
  }

  const handlePhoneNumberChange = (phone: string, provider: NameDto) => {
    setSelectedProvider(provider)
    twoFASelectPhoneModal.open({ selectedPhone: phone })
  }

  const handleAuthActionsProviderToggle = (authAction: NameDto, turnOn: boolean) => {
    if (turnOn) {
      onAuthActionsProviderToggle(true, authAction)
    } else {
      setSelectedAuthAction(authAction)
      confirmTurnOffModal.open({
        onConfirm: () => onAuthActionsProviderToggle(false, authAction),
      })
    }
  }

  const isLoading =
    isTwoFAProvidersTypesLoading || isTwoFAProvidersLoading || isTwoFAProvidersActionTypesLoading

  const isTwoFactorProvidersDataProvided =
    isTwoFactorFunctionalityEnabled &&
    twoFAProvidersTypes?.length > 0 &&
    twoFAProvidersActionTypes?.length > 0

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: t('Profile.Change_Password'),
        path: 'password-change',
      },
    ]

    if (isTwoFactorProvidersDataProvided) {
      baseTabs.push({
        label: t('TwoFactorAuth.2FA Settings'),
        path: '2fa',
      })
    }

    return baseTabs
  }, [isTwoFactorProvidersDataProvided, t])

  const outletCtx: SecuritySettingsOutletCtx = {
    activeProviders,
    twoFAProvidersTypes,
    twoFAProvidersActionTypes,
    onProviderToggle,
    onAuthActionsProviderToggle: handleAuthActionsProviderToggle,
    onPhoneNumberChange: handlePhoneNumberChange,
    onGenerateRecoveryCodes: () =>
      recoveryCodesModal.open({ initialCanClose: canCloseRecoveryCodesModal }),
    onSubmitChangePassword: handleSubmitChangePassword,
    isTwoFAProvidersLoading,
    account,
  }

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      <Box fullWidth flex direction='column' gap={isMobile ? 'x24' : 'x32'}>
        <PageSection
          id={ScrollToIds.WalletsHeader}
          title={t('Profile.Security Settings')}
          tabData={tabs.length > 1 ? { tabs } : undefined}
        />
        <Outlet context={outletCtx} />
      </Box>
    </Loading>
  )
}
